/**
 * Extracts a human-readable error message from an API/axios error.
 *
 * Handles the structured backend error shape, e.g.:
 *   {
 *     "message": "Některá pole neobsahují validní data.",
 *     "errorCode": "VALIDATION_ERROR",
 *     "details": { "detail": ["...", "..."] }
 *   }
 * as well as older shapes: an array of strings, `{ detail }`, `{ error }`,
 * a plain string, or a generic Error.
 */
export const getApiErrorMessage = (error: any, fallback = 'Něco se pokazilo'): string => {
  const data = error?.response?.data ?? error;

  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.join(', ');

  // Structured backend error: collect detail messages + top-level message.
  const detail = data.details?.detail ?? data.detail;
  const detailMessage = Array.isArray(detail)
    ? detail.join(', ')
    : typeof detail === 'string'
    ? detail
    : '';

  if (detailMessage) return detailMessage;
  if (typeof data.message === 'string') return data.message;
  if (typeof data.error === 'string') return data.error;

  return fallback;
};

/** Envelope pole standardizovaného chybového kontraktu (RFC 9457) — nejsou to názvy form fieldů. */
const ENVELOPE_KEYS = ['errorCode', 'message', 'details', 'extraData', 'traceId'];

/**
 * Vytáhne field-level validační chyby pro formuláře (Formik `setErrors`).
 * Bezpečně zvládne oba tvary backendu:
 *  - nový kontrakt: field chyby jsou pod `details` (`{ details: { email: [...] } }`)
 *  - legacy tvar: field chyby jsou přímo na top-levelu `response.data` (`{ email: [...] }`)
 * Vrací plochou mapu `fieldName -> message` (pole hlášek se spojí čárkou).
 * Nikdy nespadne a nevrací envelope meta pole jako by to byly fieldy.
 */
export const getFieldErrors = (error: any): Record<string, string> => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};

  const hasEnvelope = ENVELOPE_KEYS.some((k) => k in data);
  const source =
    hasEnvelope && data.details && typeof data.details === 'object' && !Array.isArray(data.details)
      ? data.details
      : data;

  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    if (source === data && ENVELOPE_KEYS.includes(key)) continue;
    if (value == null) continue;
    result[key] = Array.isArray(value) ? value.join(', ') : String(value);
  }
  return result;
};
